import React, { useState, useEffect } from 'react';
import Button from '@/components/shared/Button';
import { resendConfirmationEmailRequest } from '@/api';
import { useToast } from '@/components/shared/ToastProvider';
import getAxiosErrors from '@/utils/getAxiosErrors';
import './ResendConfirmationButton.scss';

const baseClass = 'ResendConfirmationButton';
const RESEND_COOLDOWN = 300; // 5 minutes in seconds

interface ResendConfirmationButtonProps {
  email: string;
}

const ResendConfirmationButton: React.FC<ResendConfirmationButtonProps> = ({ email }) => {
  const { showSuccess, showError } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await resendConfirmationEmailRequest({ email });
      showSuccess('Confirmation email has been resent.');
      setCooldown(RESEND_COOLDOWN);
    } catch (err: any) { // Corrected syntax here
      const errorData = getAxiosErrors(err);
      showError(errorData.message || 'Failed to resend email.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <Button 
      onClick={handleResend} 
      isLoading={isLoading} 
      isDisabled={cooldown > 0}
      theme="secondary"
      className={`${baseClass}--full-width`}
    >
      {cooldown > 0 ? `Resend in ${formatTime(cooldown)}` : 'Resend confirmation email'}
    </Button>
  );
};

export default ResendConfirmationButton;
